import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import IconedButton from '../../../shared/iconed-button';
import EnquiryContext from '../../../shared/enquiry/context';
import { recordStatisticsEvent } from '../../../../services/statistics-service';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  border: 1px solid ${props => props.theme.secondaryColorLight};
  border-radius: 4px;

  &.no-bottom-border-radius {
    border-radius: 4px 4px 0 0;
  }

  ${props => props.theme.isMobileOrTablet && `
    flex-direction: column;
  `};
`;

const Img = styled.img`
  border-radius: 4px;
  flex: 0 0 auto;
  margin-right: 20px;
  width: 65px;
  height: 65px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.label`
  color: ${props => props.theme.bodyColor};
  font-size: 20px;
  font-weight: ${props => props.theme.fontWeightHeavy};
  text-transform: uppercase;
`;

const Position = styled.label`
  font-size: 14px;
  font-weight: ${props => props.theme.fontWeightMedium};
  text-transform: capitalize;
  color: ${props => props.theme.bodyColor};
`;

const Divider = styled.hr`
  width: 67px;
  height: 0px;
  border-top: 2px dashed ${props => props.theme.primaryColor};
  margin: 6px 0;
`;

const Organisation = styled.label`
  color: ${props => props.theme.secondaryColor};
  font-size: 16px;
  font-weight: ${props => props.theme.fontWeightHeavy};
`;

const Actions = styled.div`
  padding: 16px;

  ${props => props.theme.isMobileOrTablet && `
    border-top: 1px solid ${props.theme.secondaryColorLight};
    width: 100%;
  `};
`;

const Button = styled(IconedButton)`
  background-color: #3c4966;
  width: 120px;
  margin: 0;

  &:hover {
    background-color: #414651;
    color: white;
  }

  & > span.icon-lw {
    font-size: 16px;
    padding: 5px;
    background-color: ${props => props.theme.bodyColor};
  }

  & > span:not(.icon-lw) {
    font-size: 14px;
  }

  ${props => props.theme.isMobile && `
    width: 100%;
  `};
`;

export default function Profile({ profile }) {
  const {
    id,
    name,
    position,
    organization,
    profilePictureUrl,
  } = profile;

  const { state: { showEnquiryForm, contactSent }, dispatch } = React.useContext(EnquiryContext);

  const handleClick = e => {
    e && e.preventDefault();

    recordStatisticsEvent({
      event: 'contributor_profile_enquiries_section_contact_clicked',
      statisticable: 'profile',
      id,
      page: window.location.pathname,
    });

    if (_.get(window, 'Livewire.currentUser.id')) {
      dispatch({ type: 'showEnquiryForm' });
    } else {
      window.location = '/sign_up?return_to_section=enquiry';
    }
  };

  const showCompanyName = profile.isCompanyProfile && organization;

  return (
    <Container className={showEnquiryForm || contactSent ? 'no-bottom-border-radius' : ''}>
      <Details>
        <Img
          src={profilePictureUrl || window.getLivewireAssetPath('avatar-fallback.png')}
          alt={name}
        />
        <Info>
          <Name>{showCompanyName ? organization : name}</Name>
          {!showCompanyName
            && <Position>{position}</Position>}
          <Divider />
          {!showCompanyName
            && <Organisation>{organization}</Organisation>}
        </Info>
      </Details>
      {!showEnquiryForm && !contactSent && (
        <Actions>
          <Button type="button" className="btn" onClick={handleClick}>
            <span className="icon-lw icon-lw-contact" />
            <span>Contact</span>
          </Button>
        </Actions>
      )}
    </Container>
  );
}

Profile.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    slug: PropTypes.string,
    profilePictureUrl: PropTypes.string,
    organization: PropTypes.string,
    position: PropTypes.string,
    isCompanyProfile: PropTypes.bool,
  }).isRequired,
};
